import { createUseStyles } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { Loading } from '@glu/indicators-react';
import classnames from 'classnames';
import Position from './Position';
import Options from './Options/Options';
import FilterInput from './FilterInput';
import Breadcrumbs from './Breadcrumbs';
import Actions from './Actions';
import styles from './Panel.styles';
import { formatOptions, filterOptionsByString } from './utils/formatOptions';
import './themeDefaults';

const useStyles = createUseStyles(styles);

const Panel = ({
  applySelection,
  className,
  dark,
  dataQa,
  emptyOptionsMessage,
  filterBy,
  filterEnabled,
  filterFunction,
  focusedIndex,
  headerComponent: HeaderComponent,
  htmlId,
  isFilterFocused,
  isLoading,
  matchBy,
  multiselect,
  onCategorySelect,
  onNextPage,
  optionComponent,
  optionListComponent,
  options,
  pressedKey,
  screenReaderLabel,
  selection,
  setFocusedIndex,
  setIsFilterFocused,
  setPressedKey,
  setSelection,
  showGroups,
  ...rest
}) => {
  const classes = useStyles();
  const endRef = useRef();
  const scrollRef = useRef();

  const [arrowDirection, setArrowDirection] = useState('down');
  const [filterString, setFilterString] = useState('');
  const [filterHtmlValue, setFilterHtmlValue] = useState(filterString);
  const [isActionsFocused, setIsActionsFocused] = useState(false);
  const [page, setPage] = useState(0);
  const [parentName, setParentName] = useState();
  const [path, setPath] = useState([]);

  const formatedOptions = formatOptions(options, parentName, showGroups);
  const filteredOptions = filterFunction(formatedOptions, filterString, filterBy, matchBy);

  const rows = 50; // Plenty of entries to create scroll bar
  const getLimit = () => (page * rows) + rows;
  const paginated = filteredOptions.slice(0, getLimit());

  const handleScroll = () => {
    const scrollPos = scrollRef.current.getBoundingClientRect().top
                      + scrollRef.current.getBoundingClientRect().height;
    const endInView = endRef.current
                      && scrollPos > endRef.current.getBoundingClientRect().top;
    if (endInView) {
      if (getLimit() <= filteredOptions.length) {
        setPage(prev => prev + 1);
        onNextPage();
      }
    }
  };

  if (pressedKey) {
    const indexOfOptionThatStartsWith = (key) => {
      const matches = paginated.reduce((acc, curr, idx) => {
        const name = curr.name.toLowerCase();
        const substr = key.toLowerCase();
        if (name.startsWith(substr)) acc.push(idx);
        return acc;
      }, []);

      if (matches.length) {
        return matches[0] + 1;
      }
      return 1;
    };
    setFocusedIndex(indexOfOptionThatStartsWith(pressedKey));
    setPressedKey(undefined);
  }

  const handleCategorySelect = (option) => {
    const pathTemp = JSON.parse(JSON.stringify(path));
    pathTemp.push(option);
    setPage(0);
    setFilterString('');
    setFilterHtmlValue('');
    scrollRef.current.scrollTop = 0;
    if (onCategorySelect) {
      onCategorySelect(option);
    } else {
      setParentName(option.id);
    }
    setPath(pathTemp);
  };

  const OptionsList = optionListComponent || Options;

  return (
    <Position
      className={classes.panel}
      dataQa="panel-position"
      screenReaderLabel={screenReaderLabel}
      {...rest}
    >
      <div className={classnames(classes.root, { [classes.dark]: dark })} id={`${htmlId}-dropdown-panel`} data-qa="dropdown-panel">
        {HeaderComponent && <HeaderComponent />}
        {filterEnabled && (
          <FilterInput
            applySelection={applySelection}
            dark={dark}
            filterBy={filterBy}
            filterHtmlValue={filterHtmlValue}
            filterString={filterString}
            htmlId={htmlId}
            isFocused={isFilterFocused}
            matchBy={matchBy}
            multiselect={multiselect}
            options={formatedOptions}
            setArrowDirection={setArrowDirection}
            setFilterHtmlValue={setFilterHtmlValue}
            setFilterString={setFilterString}
            setFocusedIndex={setFocusedIndex}
            setIsFilterFocused={setIsFilterFocused}
            setSelection={setSelection}
            {...rest}
          />
        )}
        <div
          className={classes.scroll}
          data-qa="scroll-panel"
          id={`${htmlId}-scroll-panel`}
          onScroll={handleScroll}
          ref={scrollRef}
          role="listbox"
        >
          <Breadcrumbs
            dark={dark}
            onStepSelect={onCategorySelect}
            path={path}
            setParentName={setParentName}
            setPath={setPath}
          />
          {options.length
            ? (
              <>
                <OptionsList
                  applySelection={applySelection}
                  arrowDirection={arrowDirection}
                  dark={dark}
                  filterString={filterString}
                  focusedIndex={focusedIndex}
                  handleCategorySelect={handleCategorySelect}
                  htmlId={htmlId}
                  multiselect={multiselect}
                  optionComponent={optionComponent}
                  options={paginated}
                  path={path}
                  selection={selection}
                  setArrowDirection={setArrowDirection}
                  setFocusedIndex={setFocusedIndex}
                  setIsActionsFocused={setIsActionsFocused}
                  setIsFilterFocused={setIsFilterFocused}
                  setParentName={setParentName}
                  setPath={setPath}
                  setSelection={setSelection}
                  showGroups={showGroups}
                />
                <Loading data-qa="loading-spinner" className={classes.loading} isLoading={isLoading} size={16}>
                  <div className={classes.end} ref={endRef} />
                </Loading>
                {filterEnabled && filteredOptions.length === 0
                  && (
                  <div
                    className={classnames(classes.noresults, { [classes.dark]: dark })}
                  >
                    {emptyOptionsMessage}
                  </div>
                  )}
              </>
            )
            : (
              <Loading className={classes.loading} isLoading={isLoading} size={16}>
                <div className={classnames(classes.noresults, { [classes.dark]: dark })}>
                  {emptyOptionsMessage}
                </div>
              </Loading>
            )}
        </div>
        {multiselect && (
          <Actions
            applySelection={applySelection}
            dark={dark}
            focused={isActionsFocused}
            options={filteredOptions}
            selection={selection}
            setSelection={setSelection}
            {...rest}
          />
        )}
      </div>
    </Position>
  );
};

Panel.propTypes = {
  className: PropTypes.string,
  dataQa: PropTypes.string,
  applySelection: PropTypes.func.isRequired,
  dark: PropTypes.bool.isRequired,
  emptyOptionsMessage: PropTypes.string.isRequired,
  htmlId: PropTypes.string.isRequired,
  isFilterFocused: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onCategorySelect: PropTypes.func,
  onNextPage: PropTypes.func.isRequired,
  setFocusedIndex: PropTypes.func.isRequired,
  setIsFilterFocused: PropTypes.func.isRequired,
  setSelection: PropTypes.func.isRequired,
  setPressedKey: PropTypes.func.isRequired,
  screenReaderLabel: PropTypes.bool.isRequired,

  /** Setting to determine filter algorithm. */
  filterBy: PropTypes.string.isRequired,

  /** UX setting for enabling the filter input in the options dropdown */
  filterEnabled: PropTypes.bool.isRequired,

  /** Custom filter function to be used instead of default */
  filterFunction: PropTypes.func,

  /** Index of the option which should receive focus. */
  focusedIndex: PropTypes.number,

  /** Component provided for a custom header.
   * Will render as the first item before the options and filter. */
  headerComponent: PropTypes.func,

  /** What properties to match on when filtering */
  matchBy: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** Name of the key that has been pressed when the select field was focused  */
  pressedKey: PropTypes.string,

  /** UX setting to allow multiple selections */
  multiselect: PropTypes.bool.isRequired,

  /** React component for rendering an the options list */
  optionListComponent: PropTypes.func,

  /** React component for rendering an individual option */
  optionComponent: PropTypes.func,

  /** All options for the current tree level */
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool
  })),

  /** All options currently selected */
  selection: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired
  })),

  /** Display setting for representing parent options as flat headers */
  showGroups: PropTypes.bool.isRequired
};

Panel.defaultProps = {
  className: '',
  dataQa: '',
  filterFunction: filterOptionsByString,
  focusedIndex: undefined,
  headerComponent: undefined,
  onCategorySelect: undefined,
  optionComponent: undefined,
  optionListComponent: undefined,
  options: [],
  pressedKey: undefined,
  selection: []
};

export default Panel;
